import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './BecomeOrganizer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const BecomeOrganizer: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Info, 2: Form, 3: Confirmation
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    organizerName: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Norge',
    applicantType: 'private' as 'private' | 'business',
    orgNumber: '',
    invoiceReference: ''
  });

  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Check if user already has an application
  useEffect(() => {
    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizer-applications/my-application`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (response.data.application) {
        const app = response.data.application;
        if (app.status === 'pending') {
          navigate('/deltaker/profil', { 
            state: { message: 'Du har allerede en aktiv søknad under behandling' }
          });
        } else if (app.status === 'approved') {
          navigate('/deltaker/profil', { 
            state: { message: 'Din søknad er godkjent. Du er nå arrangør!' }
          });
        }
      }
    } catch (error) {
      console.error('Error checking application:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateMod11 = (orgNumber: string): boolean => {
    const cleanNumber = orgNumber.replace(/\s/g, '');
    if (!/^\d{9}$/.test(cleanNumber)) {
      return false;
    }

    const weights = [3, 2, 7, 6, 5, 4, 3, 2];
    const digits = cleanNumber.split('').map(Number);
    const checkDigit = digits[8];

    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += digits[i] * weights[i];
    }

    const remainder = sum % 11;
    const calculatedCheckDigit = remainder === 0 ? 0 : 11 - remainder;

    return calculatedCheckDigit === checkDigit;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate org number if business
      if (formData.applicantType === 'business' && !validateMod11(formData.orgNumber)) {
        setError('Ugyldig organisasjonsnummer. Vennligst sjekk og prøv igjen.');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/organizer-applications/apply`,
        {
          organizerName: formData.organizerName,
          email: formData.email,
          phone: formData.phone,
          invoiceAddress: {
            street: formData.street,
            postalCode: formData.postalCode,
            city: formData.city,
            country: formData.country
          },
          applicantType: formData.applicantType,
          orgNumber: formData.applicantType === 'business' ? formData.orgNumber : undefined,
          invoiceReference: formData.invoiceReference
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      setSuccess(response.data.message);
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke sende søknad');
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    return (
      <div className="become-organizer-page">
        <div className="become-organizer-container">
          <h1>Bli Sykkelfest-arrangør</h1>

          <div className="pricing-info">
            <h2>Priser</h2>
            <p>Deltakere betaler ingenting. Kun arrangør betaler for arrangementet.</p>

            <div className="pricing-section">
              <h3>Arrangement – standard</h3>
              <p className="price-note">1 000 kr per sykkelfest</p>
              <ul>
                <li>Opprettelse av ett komplett Sykkelfest-arrangement</li>
                <li>Påmelding av deltakere i par</li>
                <li>Automatisk rute- og gruppefordeling</li>
                <li>Optimaliserte sykkelavstander</li>
                <li>Varsler og veiledning til deltakerne underveis</li>
                <li>Oversikt etter arrangementet</li>
                <li>Standard Sykkelfest-design</li>
              </ul>
              <p className="no-binding">Ingen binding. Ingen deltakeravgift. Ingen skjulte kostnader.</p>
            </div>

            <div className="pricing-section">
              <h3>Arrangement med tilpasset profil (white-label)</h3>
              <p className="price-note">Tillegg: 1 000 kr per arrangement</p>
              <ul>
                <li>Egen logo</li>
                <li>Egendefinerte farger</li>
                <li>Arrangørnavn synlig i hele løsningen</li>
                <li>Tilpasset landingsside</li>
                <li>Profesjonelt uttrykk</li>
              </ul>
            </div>

            <div className="startup-note">
              <h3>Oppstartsfase</h3>
              <p>Pris avtales direkte mellom systemeier og hver enkelt arrangør. Fakturering eller pilotavtale. Ingen kortbetaling i oppstartsfasen.</p>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={() => navigate('/deltaker/profil')} className="btn btn-secondary">
              Avbryt
            </button>
            <button onClick={() => setStep(2)} className="btn btn-primary">
              Fortsett til søknadsskjema
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="become-organizer-page">
        <div className="become-organizer-container">
          <h1>Søknad om å bli arrangør</h1>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="organizer-application-form">
            <div className="form-section">
              <h2>Arrangørinformasjon</h2>
              
              <div className="form-group">
                <label htmlFor="organizerName">Arrangørnavn (blir forhåndsutfylt på nye sykkelfester) *</label>
                <input
                  type="text"
                  id="organizerName"
                  name="organizerName"
                  value={formData.organizerName}
                  onChange={handleChange}
                  required
                  placeholder="F.eks. Oslo Sykkelklub"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-postadresse (blir forhåndsutfylt på nye sykkelfester) *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="kontakt@eksempel.no"
                />
                <p className="form-help">Denne e-posten må verifiseres før søknaden kan behandles</p>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefon (blir forhåndsutfylt på nye sykkelfester) *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+47 123 45 678"
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Type søker</h2>
              
              <div className="form-group">
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="applicantType"
                      value="private"
                      checked={formData.applicantType === 'private'}
                      onChange={handleChange}
                    />
                    Privat
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="applicantType"
                      value="business"
                      checked={formData.applicantType === 'business'}
                      onChange={handleChange}
                    />
                    Virksomhet
                  </label>
                </div>
              </div>

              {formData.applicantType === 'business' && (
                <div className="form-group">
                  <label htmlFor="orgNumber">Organisasjonsnummer *</label>
                  <input
                    type="text"
                    id="orgNumber"
                    name="orgNumber"
                    value={formData.orgNumber}
                    onChange={handleChange}
                    required={formData.applicantType === 'business'}
                    placeholder="123456789"
                    maxLength={9}
                  />
                  <p className="form-help">9 siffer</p>
                </div>
              )}
            </div>

            <div className="form-section">
              <h2>Fakturaadresse</h2>
              
              <div className="form-group">
                <label htmlFor="street">Gateadresse *</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  placeholder="Storgata 1"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="postalCode">Postnummer *</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    placeholder="0158"
                    maxLength={4}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="city">Poststed *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="Oslo"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="country">Land *</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="invoiceReference">Fakturareferanse</label>
                <input
                  type="text"
                  id="invoiceReference"
                  name="invoiceReference"
                  value={formData.invoiceReference}
                  onChange={handleChange}
                  placeholder="Attn: Kari Nordmann"
                />
                <p className="form-help">Valgfritt - f.eks. kontaktperson eller avdelingsnavn</p>
              </div>
            </div>

            <div className="action-buttons">
              <button type="button" onClick={() => setStep(1)} className="btn btn-secondary">
                Tilbake
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? 'Sender...' : 'Send søknad'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Step 3: Confirmation
  return (
    <div className="become-organizer-page">
      <div className="become-organizer-container">
        <h1>Søknad sendt!</h1>

        <div className="success-message">
          <p>{success}</p>
          <p>Vi har sendt en e-post til <strong>{formData.email}</strong> med en verifikasjonslenke.</p>
          <p>Vennligst verifiser e-postadressen din for å fullføre søknaden. Deretter vil en administrator behandle søknaden din.</p>
        </div>

        <div className="action-buttons">
          <button 
            onClick={() => navigate('/deltaker/profil')} 
            className="btn btn-primary"
          >
            Tilbake til Min Profil
          </button>
        </div>
      </div>
    </div>
  );
};

export default BecomeOrganizer;
